import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FaSearch, FaExclamationTriangle, FaArrowRight, FaToggleOn, FaToggleOff } from "react-icons/fa";

interface InventoryProduct {
  name: string;
  price: number;
  image: string;
  alert: string;
  productId: string;
  productTypeId: string;
  category: string;
  isActive: boolean;
  stock: number;
}

export default function WalkInInventory() {
  const [products, setProducts] = useState<InventoryProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;
  
  // Replenishment Modal State
  const [activeReplenishProduct, setActiveReplenishProduct] = useState<InventoryProduct | null>(null);
  const [replenishQty, setReplenishQty] = useState("");
  const [replenishMeasurement, setReplenishMeasurement] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Toggle Availability State
  const [togglingProducts, setTogglingProducts] = useState<Record<string, boolean>>({});

  const getToken = () => {
    const auth = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    return jsonData?.token as string | undefined;
  };

  const authHeaders = (token?: string) =>
    token ? { Authorization: `Bearer ${token}` } : undefined;

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/active-products?limit=100`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setProducts(res.data.data.data ?? []);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch inventory products");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm]);

  // Unique Categories
  const categories = useMemo(() => {
    const set = new Set(products.map(p => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [products]);

  // Filtered Products
  const filteredProducts = useMemo(() => {
    let result = products;
    if (selectedCategory !== "All") {
      result = result.filter(product => product.category === selectedCategory);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(product => 
        product.name.toLowerCase().includes(q) || 
        product.productId.toLowerCase().includes(q)
      );
    }
    return result;
  }, [products, searchTerm, selectedCategory]);

  // Client-side pagination
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleReplenishSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeReplenishProduct) return;
    const qtyNum = Number(replenishQty);
    if (!qtyNum || qtyNum <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }

    setIsUpdating(true);
    try {
      const token = getToken();
      const payload = {
        productTypeId: activeReplenishProduct.productTypeId,
        quantity: replenishQty,
        measurement: replenishMeasurement || undefined
      };

      const res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/products/${activeReplenishProduct.productId}`,
        payload,
        { headers: authHeaders(token) }
      );

      if (res.data?.success) {
        toast.success(`Successfully updated stock for ${activeReplenishProduct.name}`);
        setActiveReplenishProduct(null);
        setReplenishQty("");
        setReplenishMeasurement("");
        fetchProducts(); // Refresh list to get updated stock
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update product stock");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleAvailability = async (product: InventoryProduct) => {
    setTogglingProducts(prev => ({ ...prev, [product.productId]: true }));
    try {
      const token = getToken();
      const isAvailable = product.alert !== "Out of Stock";
      const action = isAvailable ? "out-of-stock" : "in-stock";

      const res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/products/${product.productId}/${action}`,
        {},
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        toast.success("Product availability updated successfully");
        fetchProducts(); // Refresh list to get updated status
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update product availability");
    } finally {
      setTogglingProducts(prev => ({ ...prev, [product.productId]: false }));
    }
  };

  return (
    <main className="w-full flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Physical Store Inventory</h1>
        <p className="text-gray-500 text-sm">
          Monitor in-store shelf stocks versus warehouse reserve deposits. Replenish items directly from warehouse.
        </p>
      </div>

      {/* Control Bar */}
      <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by Product Name or SKU ID..."
            className="border p-2 pl-9 focus:outline-[#008060] rounded-lg text-sm w-full"
          />
        </div>

        {/* Category Filters */}
        <div className="flex overflow-x-auto gap-2 bg-gray-50 p-1 rounded-lg">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? "bg-white text-gray-800 shadow-sm border border-gray-100"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 border-b border-gray-100">
                <th className="p-4 font-semibold">Product</th>
                <th className="p-4 font-semibold">Category</th>
                <th className="p-4 font-semibold">Stock Level</th>
                <th className="p-4 font-semibold">Price</th>
                <th className="p-4 font-semibold">Status</th>
                <th className="p-4 font-semibold text-center">Availability</th>
                <th className="p-4 font-semibold text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skeleton-${i}`}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j} className="p-4">
                        <div className="h-4 bg-gray-100 rounded animate-pulse w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : paginatedProducts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-400">
                    No products found.
                  </td>
                </tr>
              ) : (
                paginatedProducts.map((product) => {
                  const isLowStock = product.alert === "Out of Stock" || product.alert === "Low Stock";
                  
                  return (
                    <tr key={product.productId} className="hover:bg-gray-50/50 transition-colors">
                      <td className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 shrink-0">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src = ""; }} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-950">{product.name}</p>
                          <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[120px]">{product.productId}</p>
                        </div>
                      </td>
                      <td className="p-4">{product.category || "—"}</td>
                      <td className="p-4 font-semibold">{product.stock} units</td>
                      <td className="p-4 font-semibold">₦{product.price.toLocaleString()}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 w-fit ${
                          isLowStock 
                            ? "bg-red-50 text-red-700" 
                            : "bg-emerald-50 text-emerald-700"
                        }`}>
                          {isLowStock ? (
                            <>
                              <FaExclamationTriangle /> {product.alert}
                            </>
                          ) : (
                            product.alert || "In Stock"
                          )}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleToggleAvailability(product)}
                            disabled={togglingProducts[product.productId]}
                            className="focus:outline-none disabled:opacity-50 transition-all hover:scale-110 active:scale-95"
                            title={product.alert !== "Out of Stock" ? "Make Inactive" : "Make Active"}
                          >
                            {product.alert !== "Out of Stock" ? (
                              <FaToggleOn className="w-8 h-8 text-[#008060] drop-shadow-sm transition-colors" />
                            ) : (
                              <FaToggleOff className="w-8 h-8 text-gray-300 drop-shadow-sm transition-colors" />
                            )}
                          </button>
                          {togglingProducts[product.productId] && (
                            <div className="w-3 h-3 border-2 border-[#008060] border-t-transparent rounded-full animate-spin" />
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => setActiveReplenishProduct(product)}
                          className="px-3.5 py-1.5 bg-[#008060] text-white hover:bg-[#006b50] rounded-md text-xs font-semibold flex items-center gap-1.5 mx-auto transition-all shadow-sm"
                        >
                          Update Stock <FaArrowRight className="text-[10px]" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && paginatedProducts.length > 0 && (
          <div className="flex justify-between items-center p-4 border-t border-gray-100">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="text-xs font-semibold text-gray-600 disabled:text-gray-300 transition-colors"
            >
              Previous
            </button>
            <span className="text-xs text-gray-400">Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="text-xs font-semibold text-[#008060] disabled:text-gray-300 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Replenish Stock Modal */}
      {activeReplenishProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden border border-gray-100">
            <div className="p-5 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-bold text-lg text-gray-900">Update Stock</h3>
              <button 
                onClick={() => setActiveReplenishProduct(null)} 
                className="text-gray-400 hover:text-gray-600 font-bold"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleReplenishSubmit} className="p-5 flex flex-col gap-4">
              <div className="flex gap-4 items-center bg-gray-50 p-3 rounded-xl border border-gray-100">
                <img src={activeReplenishProduct.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-white" onError={(e) => { (e.target as HTMLImageElement).src = ""; }} />
                <div>
                  <p className="text-sm font-bold text-gray-800">{activeReplenishProduct.name}</p>
                  <p className="text-xs text-gray-500">Current Stock: <span className="font-bold">{activeReplenishProduct.stock} units</span></p>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-700">Quantity <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  value={replenishQty}
                  onChange={(e) => setReplenishQty(e.target.value)}
                  placeholder="Enter new stock quantity"
                  className="border p-2 focus:outline-[#008060] rounded-lg text-sm w-full"
                  required
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-700">Measurement (Optional)</label>
                <input
                  type="text"
                  value={replenishMeasurement}
                  onChange={(e) => setReplenishMeasurement(e.target.value)}
                  placeholder="e.g. 100kg"
                  className="border p-2 focus:outline-[#008060] rounded-lg text-sm w-full"
                />
              </div>

              <div className="flex gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setActiveReplenishProduct(null)}
                  disabled={isUpdating}
                  className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="flex-1 py-2 bg-[#008060] text-white hover:bg-[#006b50] rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isUpdating ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Update Stock"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
